import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IcLink, IcClose, IcSearch, IcFileText } from '../../theme/icons';
import Swal from '../../theme/swal';
import { format } from 'date-fns';
import commandeService from '../../services/commandeService';
import factureService from '../../services/factureService';
import KI from '../../theme/palette';
import { matchesSearch } from '../../utils/search';

const fmt = (v) => `${(parseFloat(v) || 0).toFixed(3)} DT`;

// Carte « Lier à une facture existante » : liste des factures du fournisseur
// (non déjà reliées à un autre bon) — choisir celle qui a été saisie sans
// passer par la réception du bon. Le bon passe à RECU et pointe sur elle.
// Partagée entre le détail du bon et la liste des bons envoyés.
const LinkFacturePopup = ({ order, onClose, onDone }) => {
  const [factures, setFactures] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [fs, orders] = await Promise.all([
          factureService.getAllFactures({ supplier_id: order.supplier }),
          commandeService.getAllOrders(),
        ]);
        // Les factures déjà reliées à un autre bon ne sont pas proposées.
        const linked = new Set((orders || [])
          .filter((o) => o.facture_id && o.id !== order.id)
          .map((o) => o.facture_id));
        if (!alive) return;
        // Un avoir (retour fournisseur) ne réceptionne pas un bon de commande.
        setFactures((fs || []).filter((f) => !linked.has(f.id) && !f.is_avoir));
        setLoading(false);
      } catch (e) {
        console.error('Erreur chargement factures:', e);
        if (!alive) return;
        onClose();
        Swal.fire('Erreur', 'Impossible de charger les factures du fournisseur', 'error');
      }
    })();
    return () => { alive = false; };
  }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyLinkFacture = async (facture) => {
    const label = facture.number || facture.barrecode || facture.id;
    const c = await Swal.fire({
      title: 'Lier cette facture ?',
      html: `Le bon <b>BC #${order.number}</b> sera marqué <b>Reçu</b> et relié à la facture <b>${label}</b>.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, lier',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.spruce,
    });
    if (!c.isConfirmed) return;
    onClose();
    try {
      await commandeService.markReceived(order.id, facture.id);
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: `Bon relié à la facture ${label}`, timer: 2200, showConfirmButton: false,
      });
      if (onDone) await onDone(facture);
    } catch (e) {
      console.error('Erreur liaison facture:', e);
      Swal.fire('Erreur',
        e.response?.data?.detail || 'Échec de la liaison à la facture', 'error');
    }
  };

  // Règle Ki Para (utils/search.js) : n° / code-barres « contient ».
  const q = (query || '').trim();
  const matches = factures.filter((f) => matchesSearch(q, [], [f.number, f.barrecode]));

  return createPortal(
    <div className="modal-backdrop z-[85] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Composants-2 §10 : entête aplat encre, titre blanc 16px 600, ✕ gris → blanc */}
        <div className="modal-head py-3.5 items-start gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="modal-title leading-tight flex items-center gap-2"><IcLink size={15} /> Lier à une facture existante</h3>
            <p className="text-xs text-gray-300 mt-0.5">
              Factures de <span className="font-semibold text-white">{order.supplier_name}</span> — le bon passera à « Reçu »
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white shrink-0" aria-label="Fermer"><IcClose size={13} /></button>
        </div>

        <div className="px-4 pt-3 shrink-0">
          <div className="relative">
            <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par n° de facture…"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 pt-3">
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Chargement des factures…</div>
          ) : matches.length === 0 ? (
            <div className="py-8 text-center">
              <IcFileText className="mx-auto text-3xl text-gray-300 mb-2" />
              <p className="text-sm text-gray-600 font-medium">Aucune facture disponible</p>
              <p className="text-xs text-gray-400 mt-1">
                {q ? 'Aucune facture ne correspond à cette recherche.'
                  : 'Ce fournisseur n\'a pas de facture non reliée à un bon.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {matches.map((f) => (
                <button
                  type="button"
                  key={f.id}
                  onClick={() => applyLinkFacture(f)}
                  className="w-full px-3.5 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-100 flex items-center justify-between gap-3 text-left transition-colors"
                >
                  <span className="min-w-0">
                    <span className="block font-semibold text-gray-800 truncate">
                      Facture {f.number || f.barrecode || f.id}
                    </span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      {f.date_added ? format(new Date(f.date_added), 'dd/MM/yyyy HH:mm') : '—'}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block font-bold text-gray-900 tabular-nums">{fmt(f.total)}</span>
                    {/* Facture fournisseur (Etiquettes-2 §3) : ✓ Payé · ◐ Partiel · ● Non payé */}
                    {(parseFloat(f.amount_due) || 0) <= 0 ? (
                      <span className="bdg bdg-pos mt-0.5">✓ Payé</span>
                    ) : (parseFloat(f.amount_due) || 0) < (parseFloat(f.total) || 0) ? (
                      <span className="bdg bdg-alert mt-0.5">◐ Partiel</span>
                    ) : (
                      <span className="bdg bdg-critical mt-0.5">● Non payé</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LinkFacturePopup;
